
import * as XLSX from 'xlsx';
import { CreateSupermarketPriceInput } from '@/types/database/supermarket-prices';
import { parseExcelDate } from './utils/dateUtils';
import { getColumnValue } from './utils/columnUtils';

export interface SupermarketExcelRow {
  'Timestamp': string | Date;
  'Date': string | Date;
  'Products List': string;
  'Product List': string;
  'Product Name': string;
  'Unit Price': string | number;
  'Price': string | number;
  'Location': string;
  'Product Remarks'?: string;
  'Product Remark'?: string;
  'Remark'?: string;
  'Notes'?: string;
}

export const parseSupermarketPricesFromExcel = (workbook: XLSX.WorkBook): {
  data: CreateSupermarketPriceInput[];
  errors: string[];
} => {
  const errors: string[] = [];
  const data: CreateSupermarketPriceInput[] = [];

  try {
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      errors.push('No sheets found in the Excel file');
      return { data, errors };
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      defval: null,
      raw: false 
    }) as any[][];

    if (jsonData.length < 2) {
      errors.push('Excel file must contain at least a header row and one data row');
      return { data, errors };
    }

    const headers = jsonData[0] as string[];
    const rows = jsonData.slice(1);

    console.log('Excel headers found:', headers);

    rows.forEach((row, index) => {
      const rowIndex = index + 2;
      
      try {
        const rowObj: Record<string, any> = {};
        headers.forEach((header, colIndex) => {
          if (header) {
            rowObj[header] = row[colIndex];
          }
        });

        if (Object.values(rowObj).every(value => !value)) {
          return;
        }

        // Map Date from Excel Timestamp (fallback to Date)
        const dateValue = getColumnValue(rowObj, ['Timestamp', 'Date']);
        const parsedDate = parseExcelDate(dateValue);
        
        if (!parsedDate) {
          errors.push(`Row ${rowIndex}: Invalid or missing date in Timestamp/Date column`);
          return;
        }

        // Map Product Name from Excel Products List (fallback to Product List, Product Name)
        const productName = getColumnValue(rowObj, ['Products List', 'Product List', 'Product Name']);
        if (!productName || productName.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Missing product name in Products List/Product List/Product Name column`);
          return;
        }

        // Map Price from Excel Unit Price (fallback to Price)
        const priceValue = getColumnValue(rowObj, ['Unit Price', 'Price']);
        let price = 0;
        
        if (priceValue !== null && priceValue !== undefined) {
          const priceStr = priceValue.toString().replace(/[^\d.-]/g, '');
          price = parseFloat(priceStr);
          
          if (isNaN(price)) {
            errors.push(`Row ${rowIndex}: Invalid price value "${priceValue}" in Unit Price/Price column`);
            return;
          }
        }

        const location = getColumnValue(rowObj, ['Location']);
        if (!location || location.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Missing location in Location column`);
          return;
        }

        // Map Product Remark from Excel Product Remarks (fallback to Product Remark, Remark, Notes)
        const productRemarkValue = getColumnValue(rowObj, ['Product Remarks', 'Product Remark', 'Remark', 'Notes']);
        let productRemark: string | null = null;
        
        if (productRemarkValue !== null && productRemarkValue !== undefined) {
          const remarkStr = String(productRemarkValue).trim();
          
          if (remarkStr && 
              remarkStr !== 'undefined' && 
              remarkStr !== 'null' &&
              remarkStr !== 'N/A' &&
              remarkStr !== '') {
            productRemark = remarkStr;
          }
        }

        const supermarketPrice: CreateSupermarketPriceInput = {
          date: parsedDate,
          product_name: productName.toString().trim(),
          price: price,
          location: location.toString().trim(),
          product_remark: productRemark,
        };

        console.log(`Row ${rowIndex} parsed:`, supermarketPrice);
        data.push(supermarketPrice);
        
      } catch (error) {
        console.error(`Error parsing row ${rowIndex}:`, error);
        errors.push(`Row ${rowIndex}: ${error instanceof Error ? error.message : 'Unknown parsing error'}`);
      }
    });

    console.log(`Successfully parsed ${data.length} records with ${errors.length} errors`);

  } catch (error) {
    console.error('Error parsing supermarket prices from Excel:', error);
    errors.push(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return { data, errors };
};
